/**
 * El menú lateral (drawer).
 *
 * Client Component: abre, cierra y maneje el foco. Al cerrarse, el foco
 * vuelve al botón que lo abrió, y con Escape se cierra.
 */
'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

import { icons } from './icons';
import type { ItemNav, SeccionMenu } from './header';

export interface Props {
  menu: SeccionMenu[];
  actuales?: string[];
}

export function Drawer({ menu, actuales = [] }: Props) {
  const [abierto, setAbierto] = useState(false);
  const botonAbrir = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);

  // Escape cierra el menú.
  useEffect(() => {
    if (!abierto) return;
    const onKey = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [abierto]);

  // Al cerrar, el foco vuelve al botón que lo abrió.
  useEffect(() => {
    if (abierto) return;
    botonAbrir.current?.focus?.();
  }, [abierto]);

  // El fondo no debe scrollear con el menú abierto.
  useEffect(() => {
    document.body.style.overflow = abierto ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [abierto]);

  // El foco no se va del menú abierto.
  function trapping(evento: React.KeyboardEvent<HTMLElement>) {
    if (evento.key !== 'Tab') return;
    const focusables = panel.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
    if (!focusables?.length) return;
    const primero = focusables[0];
    const ultimo = focusables[focusables.length - 1];
    if (evento.shiftKey && document.activeElement === primero) {
      evento.preventDefault();
      ultimo.focus();
    } else if (!evento.shiftKey && document.activeElement === ultimo) {
      evento.preventDefault();
      primero.focus();
    }
  }

  return (
    <>
      <button
        ref={botonAbrir}
        className="icon-btn"
        type="button"
        onClick={() => setAbierto(true)}
        aria-label="Abrir menú"
        aria-expanded={abierto}
        aria-controls="menu-lateral"
      >
        {icons.menu}
      </button>

      <div
        className={`drawer__backdrop${abierto ? ' is-open' : ''}`}
        onClick={() => setAbierto(false)}
        hidden={!abierto}
        aria-hidden="true"
      />

      <aside
        ref={panel}
        className={`drawer${abierto ? ' is-open' : ''}`}
        id="menu-lateral"
        aria-label="Menú"
        onKeyDown={trapping}
        hidden={!abierto}
      >
        <div className="drawer__head">
          <span className="drawer__title">Menú</span>
          <button
            className="icon-btn"
            type="button"
            onClick={() => setAbierto(false)}
            aria-label="Cerrar menú"
          >
            {icons.close}
          </button>
        </div>
        {menu.map((section) => (
          <div className="drawer__section" key={section.title}>
            <p className="drawer__section-title">{section.title}</p>
            {section.links.map((link: ItemNav) => (
              <Link
                key={link.href}
                className="drawer__link"
                href={link.href}
                {...(actuales.includes(link.href) ? { 'aria-current': 'page' as const } : {})}
              >
                {link.label}
              </Link>
            ))}
          </div>
        ))}
      </aside>
    </>
  );
}
